import { useState } from "react";
import { toast } from "sonner";
import type { TaskSummary, TaskUpdate } from "@patent-studio/api-types";
import { useUpdateTask } from "@/hooks/use-tasks";
import { refusalDetail } from "@/services/api-client";
import {
  type DetailsForm,
  DraftDetailsDialog,
  deadlineAllowed,
  isComplete,
} from "./DraftDetailsFields";
import { resolveDraftTitle } from "../layout/draft-title";

/** The draft's details as the form opens on them: the name the card shows, and its inventors by id. */
function formOf(draft: TaskSummary): DetailsForm {
  return {
    title: resolveDraftTitle(draft),
    inventors: draft.inventors.map(({ inventor_id, name }) => ({
      inventor_id,
      name,
    })),
    jurisdiction: draft.jurisdiction ?? "USPTO",
    deadline: draft.filing_deadline ?? "",
  };
}

/**
 * What a save writes. The name goes to the title, and to the working title too
 * when the draft has one, since the card and the header read that first. The
 * office and the deadline go only when they changed, so a deadline already
 * passed does not stop the other details being saved; moved, it is today or later.
 */
function editOf(draft: TaskSummary, form: DetailsForm): TaskUpdate {
  const title = form.title.trim();
  return {
    title,
    ...(draft.working_title === null ? {} : { working_title: title }),
    inventors: form.inventors,
    ...(form.jurisdiction === draft.jurisdiction
      ? {}
      : { jurisdiction: form.jurisdiction }),
    ...(form.deadline === draft.filing_deadline
      ? {}
      : { filing_deadline: form.deadline }),
  };
}

/**
 * Changes the details a draft in progress was started with. The fields are the
 * new draft modal's, opened on what the draft holds; the server refuses the
 * change once generation has started, and that refusal is told in its words.
 */
export function EditDraftDialog({
  draft,
  onClose,
}: {
  draft: TaskSummary;
  onClose: () => void;
}) {
  const [form, setForm] = useState<DetailsForm>(() => formOf(draft));
  const save = useUpdateTask(draft.task_id);

  return (
    <DraftDetailsDialog
      title="Edit draft details"
      description="Changes apply to this draft only."
      form={form}
      onChange={(change) => {
        setForm((was) => ({ ...was, ...change }));
      }}
      clientId={draft.client_id ?? null}
      submitLabel={save.isPending ? "Saving…" : "Save Changes"}
      ready={
        isComplete(form) &&
        deadlineAllowed(form.deadline, draft.filing_deadline) &&
        !save.isPending
      }
      onSubmit={() => {
        save.mutate(editOf(draft, form), {
          onSuccess: () => {
            toast.success("Draft details updated.");
            onClose();
          },
          onError: (error) => {
            toast.error(
              refusalDetail(error, "The details could not be saved."),
            );
          },
        });
      }}
      onClose={onClose}
    />
  );
}
